import { createContext, type ReactNode, useContext } from "react";

/**
 * Whether the nav rail is currently showing labels.
 *
 * Replaces `useSidebarOpenState` from `@backstage/core-components`, which is
 * published by that package's Material UI `Sidebar`. We no longer render that
 * component, so the state is owned by {@link SidebarRail} instead.
 *
 * "Open" is not the same as "pinned": the rail is open when pinned *or* while
 * hovered/focused. Pinned additionally shifts page content — see
 * `AppLayout.module.css`.
 */
const SidebarOpenStateContext = createContext(false);

/** Publishes the rail's open state to its descendants. */
export const SidebarOpenStateProvider = (props: {
    isOpen: boolean;
    children: ReactNode;
}) => (
    <SidebarOpenStateContext.Provider value={props.isOpen}>
        {props.children}
    </SidebarOpenStateContext.Provider>
);

/**
 * Returns whether the nav rail is expanded.
 *
 * Defaults to `false` (collapsed) outside a provider, which is the safe
 * fallback: items render icon-only with a tooltip rather than overflowing a
 * 72px rail with labels.
 */
export const useSidebarOpen = () => useContext(SidebarOpenStateContext);
